// src/pages/ProfileBankDetails.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

export default function ProfileBankDetails() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [user, setUser] = useState(null);

  // ── Personal Info (read-only fields kept for display only) ──
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [userID, setUserID] = useState("");
  const [userType, setUserType] = useState("");

  // ── Editable personal fields ──
  const [gender, setGender] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [adharNumber, setAdharNumber] = useState("");

  // ── Bank / PAN / UPI ──
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankBranch, setBankBranch] = useState("");
  const [bankAccountType, setBankAccountType] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [upiId, setUpiId] = useState("");

  // ── Nominee ──
  const [nomineeName, setNomineeName] = useState("");
  const [nomineeRelation, setNomineeRelation] = useState("");
  const [nomineePhone, setNomineePhone] = useState("");

  // ── Image files (new uploads) ──
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [bankPhotoFile, setBankPhotoFile] = useState(null);
  const [panPhotoFile, setPanPhotoFile] = useState(null);
  const [aadharPhotoFile, setAadharPhotoFile] = useState(null);

  // ── Image previews ──
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [bankPhotoPreview, setBankPhotoPreview] = useState(null);
  const [panPhotoPreview, setPanPhotoPreview] = useState(null);
  const [aadharPhotoPreview, setAadharPhotoPreview] = useState(null);

  const token = useMemo(
    () => localStorage.getItem("token") || localStorage.getItem("authToken"),
    []
  );

  const toast = (msg) => {
    setOk(msg);
    window.clearTimeout(window.__tok__);
    window.__tok__ = window.setTimeout(() => setOk(""), 2800);
  };

  // ── Persist image URLs in localStorage so they survive page refresh ──
  const STORAGE_KEY = "profile_image_urls";

  const saveImageUrlsToStorage = (urls) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(urls)); } catch { }
  };

  const loadImageUrlsFromStorage = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
  };

  const getImgUrl = (path) => {
    if (!path) return null;
    const p = String(path).trim();
    if (!p) return null;
    if (p.startsWith("http://") || p.startsWith("https://")) return p;

    let base = (axiosInstance?.defaults?.baseURL || "").trim();
    let origin = window.location.origin;
    try {
      if (base) origin = new URL(base).origin;
    } catch { /* ignore */ }

    origin = origin.replace(/\/$/, "");
    return `${origin}/${p.replace(/^\//, "")}`;
  };

  // ── Resolve image from multiple possible field names ──
  const resolveImg = (u, ...keys) => {
    for (const key of keys) {
      const val = u?.[key];
      if (val && typeof val === "string" && val.trim()) return getImgUrl(val.trim());
    }
    return null;
  };

  const setFromUser = (u) => {
    setUser(u);

    // read-only
    setName(u?.name || "");
    setPhone(u?.phone || "");
    setEmail(u?.email || "");
    setUserID(u?.userID || "");
    setUserType(u?.userType || "");

    // editable personal
    setGender(u?.gender || "");
    setDateOfBirth(u?.dateOfBirth || "");
    setAdharNumber(u?.adharNumber || "");

    // bank
    setBankAccountNumber(u?.bankAccountNumber || "");
    setIfscCode(u?.ifscCode || "");
    setAccountHolderName(u?.accountHolderName || "");
    setBankName(u?.bankName || "");
    setBankBranch(u?.bankBranch || "");
    setBankAccountType(u?.bankAccountType || "");
    setPanNumber(u?.panNumber || "");
    setUpiId(u?.upiId || "");

    // nominee
    setNomineeName(u?.nomineeName || "");
    setNomineeRelation(u?.nomineeRelation || "");
    setNomineePhone(u?.nomineePhone || "");

    // reset file selections
    setProfilePicFile(null);
    setBankPhotoFile(null);
    setPanPhotoFile(null);
    setAadharPhotoFile(null);

    // ── Image previews — covers all common field name variations ──
    const profileUrl = resolveImg(u,
      "profilePic", "profilePicUrl", "profilePhoto", "profilePhotoUrl",
      "profile_pic", "profile_photo", "avatar", "avatarUrl", "photo", "pic"
    );
    const bankUrl = resolveImg(u,
      "bankPhoto", "bankPhotoUrl", "bankPassbook", "bankPassbookUrl",
      "passbookPhoto", "passbookUrl", "bank_photo", "bank_passbook",
      "chequePhoto", "cancelledCheque"
    );
    const panUrl = resolveImg(u,
      "panPhoto", "panPhotoUrl", "panCard", "panCardUrl",
      "panImage", "pan_photo", "pan_card", "panCardPhoto"
    );
    const aadharUrl = resolveImg(u,
      "aadharPhoto", "aadharPhotoUrl", "aadharCard", "aadharCardUrl",
      "aadharImage", "adharPhoto", "adharPhotoUrl", "adharCard",
      "aadhaarPhoto", "aadhaarCard", "aadhar_photo", "adhar_photo"
    );

    // ── Load from localStorage as fallback if API didn't return images ──
    const stored = loadImageUrlsFromStorage();

    const finalProfile = profileUrl || stored.profile || null;
    const finalBank = bankUrl || stored.bank || null;
    const finalPan = panUrl || stored.pan || null;
    const finalAadhar = aadharUrl || stored.aadhar || null;

    // ── Save whatever we have to localStorage for next page load ──
    saveImageUrlsToStorage({
      profile: finalProfile,
      bank: finalBank,
      pan: finalPan,
      aadhar: finalAadhar,
    });

    setProfilePicPreview(finalProfile);
    setBankPhotoPreview(finalBank);
    setPanPhotoPreview(finalPan);
    setAadharPhotoPreview(finalAadhar);
  };

  const loadMe = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await axiosInstance.get("/api/users/me", { params: { _ts: Date.now() } });
      const u = res?.data?.user || res?.data?.data || res?.data || null;
      if (!u) throw new Error("Profile data not found");

      // ── DEBUG (remove after fix) ──
      console.log("FULL API RESPONSE:", JSON.stringify(res?.data, null, 2));

      setFromUser(u);
    } catch (e) {
      setUser(null);
      setErr(
        e?.response?.data?.msg ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load profile"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) { navigate("/login", { replace: true }); return; }
    loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageChange = (file, setFile, setPreview) => {
    if (!file) return;
    setFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const saveAll = async () => {
    if (!user?.id) return;

    if (nomineePhone && !/^\d{10}$/.test(nomineePhone)) {
      setErr("Nominee Phone must be exactly 10 digits.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (adharNumber && !/^\d{12}$/.test(adharNumber)) {
      setErr("Aadhar Number must be exactly 12 digits.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (panNumber && String(panNumber).trim().length !== 10) {
      setErr("PAN Number must be exactly 10 characters.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (bankAccountNumber && !/^\d+$/.test(bankAccountNumber)) {
      setErr("Bank Account Number must contain only digits.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setSaving(true);
      setErr("");
      setOk("");

      const fd = new FormData();
      const add = (k, v) => fd.append(k, String(v ?? "").trim());

      add("gender", gender);
      add("dateOfBirth", dateOfBirth);
      add("adharNumber", adharNumber);
      add("accountHolderName", accountHolderName);
      add("bankAccountNumber", bankAccountNumber);
      add("ifscCode", ifscCode);
      add("bankName", bankName);
      add("bankBranch", bankBranch);
      add("bankAccountType", bankAccountType);
      add("panNumber", panNumber);
      add("upiId", upiId);
      add("nomineeName", nomineeName);
      add("nomineeRelation", nomineeRelation);
      add("nomineePhone", nomineePhone);

      if (profilePicFile) fd.append("profilePic", profilePicFile);
      if (bankPhotoFile) fd.append("bankPhoto", bankPhotoFile);
      if (panPhotoFile) fd.append("panPhoto", panPhotoFile);
      if (aadharPhotoFile) fd.append("aadharPhoto", aadharPhotoFile);

      const res = await axiosInstance.put(`/api/users/${user.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updated =
        res?.data?.user ||
        res?.data?.updatedUser ||
        res?.data?.data ||
        res?.data ||
        null;

      // ── Capture current previews BEFORE setFromUser resets them ──
      const currentPreviews = {
        profile: profilePicPreview,
        bank: bankPhotoPreview,
        pan: panPhotoPreview,
        aadhar: aadharPhotoPreview,
      };

      // ── Save to localStorage immediately so page refresh shows images ──
      saveImageUrlsToStorage(currentPreviews);

      if (updated?.id) {
        setFromUser(updated);
        // Restore previews if server response didn't include image URLs
        setTimeout(() => {
          setProfilePicPreview(prev => prev || currentPreviews.profile);
          setBankPhotoPreview(prev => prev || currentPreviews.bank);
          setPanPhotoPreview(prev => prev || currentPreviews.pan);
          setAadharPhotoPreview(prev => prev || currentPreviews.aadhar);
        }, 0);
      } else {
        // Re-fetch but restore previews if /me doesn't return images
        const res2 = await axiosInstance.get("/api/users/me", { params: { _ts: Date.now() } });
        const u2 = res2?.data?.user || res2?.data?.data || res2?.data || null;
        if (u2) setFromUser(u2);
        setTimeout(() => {
          setProfilePicPreview(prev => prev || currentPreviews.profile);
          setBankPhotoPreview(prev => prev || currentPreviews.bank);
          setPanPhotoPreview(prev => prev || currentPreviews.pan);
          setAadharPhotoPreview(prev => prev || currentPreviews.aadhar);
        }, 0);
      }

      toast("Profile updated successfully ✅");
      window.alert("Your details have been updated successfully!");
    } catch (e) {
      setErr(
        e?.response?.data?.msg ||
        e?.response?.data?.message ||
        e?.message ||
        "Update failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePicOnlyUpdate = async () => {
    if (!profilePicFile || !user?.id) return;
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append("profilePic", profilePicFile);
      await axiosInstance.put(`/api/users/${user.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      window.alert("Pic Uploaded ✅");
      loadMe();
    } catch (e) {
      setErr("Failed to upload picture. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const avatarLetter = (user?.name || "U").trim().slice(0, 1).toUpperCase();

  const userTypeLabel = (v) => {
    const map = {
      TRAINEE_ENTREPRENEUR: "Trainee Entrepreneur",
      ENTREPRENEUR: "Entrepreneur",
      ADMIN: "Admin",
      USER: "User",
    };
    return map[v] || v || "—";
  };

  return (
    <div style={S.page}>
      <style>{css}</style>
      <div style={S.container}>

        {loading && <div style={S.alert}>Loading…</div>}
        {err && <div style={{ ...S.alert, ...S.alertErr }}>{err}</div>}
        {ok && <div style={{ ...S.alert, ...S.alertOk }}>{ok}</div>}

        {!loading && user && (
          <>
            {/* ══ PROFILE PICTURE SECTION ══ */}
            <div style={S.header}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
                <ImageUploadCircle
                  preview={profilePicPreview}
                  letter={avatarLetter}
                  label="Profile Picture"
                  onFile={(f) =>
                    handleImageChange(f, setProfilePicFile, setProfilePicPreview)
                  }
                />
                
                {profilePicFile && (
                  <button 
                    style={{ ...S.btnSave, padding: "8px 16px", fontSize: 12 }}
                    onClick={handlePicOnlyUpdate}
                    disabled={saving}
                  >
                    {saving ? "Uploading..." : "Click here to Update Picture"}
                  </button>
                )}

                <div style={{ textAlign: "center" }}>
                  <div style={{ ...S.headerName, marginBottom: 4 }}>{user?.name || "—"}</div>
                  <div style={{ ...S.pills, justifyContent: "center" }}>
                    <span style={{
                      ...S.pill,
                      backgroundColor: "#0891b2",
                      color: "#ffffff",
                      borderColor: "#0e7490",
                    }}>
                      {String(user?.role || "USER").toUpperCase()}
                    </span>
                    <span style={{
                      ...S.pill2,
                      backgroundColor:
                        user?.userType === "ENTREPRENEUR" ? "#16a34a"
                          : user?.userType === "TRAINEE_ENTREPRENEUR" ? "#ef4444"
                            : "rgba(245,158,11,.10)",
                      color: (user?.userType === "ENTREPRENEUR" || user?.userType === "TRAINEE_ENTREPRENEUR")
                        ? "#ffffff" : "#92400e",
                      borderColor:
                        user?.userType === "ENTREPRENEUR" ? "#15803d"
                          : user?.userType === "TRAINEE_ENTREPRENEUR" ? "#b91c1c"
                            : "rgba(245,158,11,.25)",
                    }}>
                      {userTypeLabel(user?.userType)}
                    </span>
                  </div>
                  <div style={{ ...S.headerSub, justifyContent: "center", marginTop: 8 }}>
                    <span><b>ID:</b> {user?.userID || "—"}</span>
                    <span style={S.dot}>·</span>
                    <span><b>Phone:</b> {user?.phone || "—"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ══ READ-ONLY ACCOUNT INFO ══ */}
            <div style={S.section}>
              <div style={S.sectionTitle}>
                <span style={S.icon}>🔒</span> Account Info
              </div>
              <div className="grid2">
                <ROField label="Full Name" value={name} />
                <ROField label="Mobile" value={phone} />
                <ROField label="Email" value={email} />
                <ROField label="User ID" value={userID} />
                <ROField label="User Type" value={userTypeLabel(userType)} />
              </div>
            </div>

            {/* ══ PERSONAL INFO ══ */}
            <div style={S.section}>
              <div style={S.sectionTitle}>
                <span style={S.icon}>👤</span> Personal Information
              </div>
              <div className="grid2">
                <Field label="Gender">
                  <select style={S.input} value={gender} onChange={(e) => setGender(e.target.value)}>
                    <option value="">Select gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </Field>
                <Field label="Date of Birth">
                  <input
                    style={S.input}
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                  />
                </Field>
                <Field label="Aadhar Number">
                  <input
                    style={S.input}
                    value={adharNumber}
                    onChange={(e) => setAdharNumber(e.target.value.replace(/\D/g, ""))}
                    placeholder="12-digit Aadhar number"
                    maxLength={12}
                  />
                </Field>
              </div>
            </div>

            {/* ══ BANK / PAN / UPI ══ */}
            <div style={S.section}>
              <div style={S.sectionTitle}>
                <span style={S.icon}>🏦</span> Bank / PAN / UPI Details
              </div>
              <div className="grid2">
                <Field label="Account Holder Name">
                  <input style={S.input} value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                    placeholder="Account holder name" />
                </Field>
                <Field label="Bank Account Number">
                  <input style={S.input} value={bankAccountNumber}
                    onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, ""))}
                    placeholder="Account number" />
                </Field>
                <Field label="IFSC Code">
                  <input style={S.input} value={ifscCode}
                    onChange={(e) => setIfscCode(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())}
                    placeholder="IFSC Code" />
                </Field>
                <Field label="Bank Name">
                  <input style={S.input} value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="Bank name" />
                </Field>
                <Field label="Bank Branch">
                  <input style={S.input} value={bankBranch}
                    onChange={(e) => setBankBranch(e.target.value)}
                    placeholder="Branch" />
                </Field>
                <Field label="Account Type">
                  <select style={S.input} value={bankAccountType}
                    onChange={(e) => setBankAccountType(e.target.value)}>
                    <option value="">Select type</option>
                    <option value="SAVINGS">Savings</option>
                    <option value="CURRENT">Current</option>
                    <option value="SALARY">Salary</option>
                  </select>
                </Field>
                <Field label="PAN Number">
                  <input style={S.input} value={panNumber}
                    onChange={(e) => setPanNumber(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())}
                    placeholder="ABCDE1234F" maxLength={10} />
                </Field>
                <Field label="UPI ID">
                  <input style={S.input} value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="example@upi" />
                </Field>
              </div>
            </div>

            {/* ══ NOMINEE ══ */}
            <div style={S.section}>
              <div style={S.sectionTitle}>
                <span style={S.icon}>🧑‍🤝‍🧑</span> Nominee Details
              </div>
              <div className="grid2">
                <Field label="Nominee Name">
                  <input style={S.input} value={nomineeName}
                    onChange={(e) => setNomineeName(e.target.value)}
                    placeholder="Full name of nominee" />
                </Field>
                <Field label="Relation with Nominee">
                  <select style={S.input} value={nomineeRelation}
                    onChange={(e) => setNomineeRelation(e.target.value)}>
                    <option value="">Select relation</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Father">Father</option>
                    <option value="Mother">Mother</option>
                    <option value="Son">Son</option>
                    <option value="Daughter">Daughter</option>
                    <option value="Brother">Brother</option>
                    <option value="Sister">Sister</option>
                    <option value="Grand Father">Grand Father</option>
                    <option value="Grand Mother">Grand Mother</option>
                    <option value="Other">Other</option>
                  </select>
                </Field>
                <Field label="Nominee Phone">
                  <input style={S.input} value={nomineePhone}
                    onChange={(e) => setNomineePhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="10-digit mobile number" maxLength={10} />
                </Field>
              </div>
            </div>

            {/* ══ DOCUMENT UPLOADS ══ */}
            <div style={S.section}>
              <div style={S.sectionTitle}>
                <span style={S.icon}>📎</span> Document Photos
              </div>
              <div className="grid2">
                <DocUpload
                  label="Bank Passbook / Cheque"
                  sublabel="Front page of passbook or cancelled cheque"
                  preview={bankPhotoPreview}
                  icon="🏦"
                  onFile={(f) =>
                    handleImageChange(f, setBankPhotoFile, setBankPhotoPreview)
                  }
                />
                <DocUpload
                  label="PAN Card"
                  sublabel="Clear photo of your PAN card"
                  preview={panPhotoPreview}
                  icon="🪪"
                  onFile={(f) =>
                    handleImageChange(f, setPanPhotoFile, setPanPhotoPreview)
                  }
                />
                <DocUpload
                  label="Aadhar Card"
                  sublabel="Front side of your Aadhar card"
                  preview={aadharPhotoPreview}
                  icon="📋"
                  onFile={(f) =>
                    handleImageChange(f, setAadharPhotoFile, setAadharPhotoPreview)
                  }
                />
              </div>
            </div>

            {/* ── ACTIONS ── */}
            <div style={S.actions}>
              <button style={S.btnReset} onClick={loadMe} disabled={saving}>
                Reset
              </button>
              <button style={S.btnSave} onClick={saveAll} disabled={saving}>
                {saving ? "Saving…" : "Save All Changes"}
              </button>
            </div>
          </>
        )}

        {!loading && !user && !err && (
          <div style={S.alert}>No data found.</div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Read-Only Field
══════════════════════════════════════════════════════════════════ */
function ROField({ label, value }) {
  const handleClick = () => {
    window.alert("Please contact our Administrator to change your " + label + ".");
  };
  return (
    <div style={S.field}>
      <div style={S.label}>{label}</div>
      <div style={{ ...S.roBox, cursor: "pointer" }} onClick={handleClick}>
        <span style={S.roValue}>{value || "—"}</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Profile Picture Upload (circular)
══════════════════════════════════════════════════════════════════ */
function ImageUploadCircle({ preview, letter, label, onFile }) {
  const ref = useRef();
  return (
    <div style={IC.wrap}>
      <div
        style={{
          ...IC.circle,
          borderColor: preview ? "rgba(22, 163, 74, 0.4)" : "rgba(245, 158, 11, 0.4)"
        }}
        className="avatarCircle"
        onClick={() => ref.current.click()}
        title={`Change ${label}`}
      >
        {preview ? (
          <img src={preview} alt={label} style={IC.img} />
        ) : (
          <span style={IC.letter}>{letter}</span>
        )}
        <div style={IC.overlay} className="avatarOverlay">
          <span style={{ fontSize: 18 }}>📷</span>
          <span style={{ fontSize: 9, color: "#fff", marginTop: 2, fontWeight: 700 }}>CHANGE</span>
        </div>
      </div>
      <div style={IC.label}>{label}</div>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => onFile(e.target.files[0])}
      />
    </div>
  );
}

const IC = {
  wrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, flexShrink: 0 },
  circle: {
    width: 84, height: 84, borderRadius: "50%",
    border: "2.5px solid rgba(245,158,11,.4)",
    background: "linear-gradient(135deg,rgba(245,158,11,.18),rgba(2,132,199,.1))",
    display: "grid", placeItems: "center",
    cursor: "pointer", position: "relative", overflow: "hidden",
  },
  img: { width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" },
  letter: { fontSize: 32, fontWeight: 800, color: "#92400e" },
  overlay: {
    position: "absolute", inset: 0, background: "rgba(0,0,0,0.42)",
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", opacity: 0, transition: "opacity .2s", borderRadius: "50%",
  },
  label: { fontSize: 11, color: "#64748b", fontWeight: 600 },
};

/* ══════════════════════════════════════════════════════════════════
   Document Photo Upload (card) — shows image on load if available
══════════════════════════════════════════════════════════════════ */
function DocUpload({ label, sublabel, preview, icon, onFile }) {
  const ref = useRef();
  const [hov, setHov] = useState(false);

  return (
    <div
      style={{
        ...DC.card,
        borderColor: hov
          ? "rgba(245,158,11,.5)"
          : preview
            ? "rgba(34,197,94,.35)"
            : "rgba(15,23,42,.18)",
        background: hov ? "rgba(245,158,11,.04)" : "#fafafa",
      }}
      onClick={() => ref.current.click()}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {/* ── Image preview (shown on page load if server returns image) ── */}
      {preview ? (
        <div style={DC.previewWrap}>
          <img
            src={preview}
            alt={label}
            style={DC.previewImg}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          {hov && (
            <div style={DC.previewOverlay}>
              <span style={{ fontSize: 20 }}>🔄</span>
              <span style={{ fontSize: 11, color: "#fff", marginTop: 3 }}>Change</span>
            </div>
          )}
        </div>
      ) : (
        <div style={DC.placeholder}>
          <span style={DC.iconBig}>{icon}</span>
          <span style={DC.uploadBadge}>⬆</span>
        </div>
      )}

      <div style={DC.info}>
        <div style={DC.docLabel}>{label}</div>
        <div style={DC.docSub}>{sublabel}</div>
        <div style={{ ...DC.docHint, color: preview ? "#16a34a" : "#94a3b8" }}>
          {preview ? "✅ Uploaded — click to change" : "Click to upload image"}
        </div>
      </div>

      <input
        ref={ref}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: "none" }}
        onChange={(e) => onFile(e.target.files[0])}
      />
    </div>
  );
}

const DC = {
  card: {
    border: "1.5px dashed", borderRadius: 14, padding: "14px 16px", cursor: "pointer",
    display: "flex", gap: 14, alignItems: "center", transition: "border-color .2s, background .2s",
  },
  previewWrap: {
    width: 90, height: 90, borderRadius: 10, overflow: "hidden",
    flexShrink: 0, position: "relative",
    border: "1.5px solid rgba(34,197,94,.3)",
  },
  previewImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  previewOverlay: {
    position: "absolute", inset: 0, background: "rgba(0,0,0,.45)",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
  },
  placeholder: {
    width: 90, height: 90, borderRadius: 10, flexShrink: 0,
    background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.2)",
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative", flexDirection: "column", gap: 4,
  },
  iconBig: { fontSize: 28 },
  uploadBadge: {
    position: "absolute", bottom: 4, right: 4, fontSize: 12,
    background: "rgba(245,158,11,.2)", borderRadius: 4, padding: "1px 3px",
  },
  info: { flex: 1, minWidth: 0 },
  docLabel: { fontSize: 14.5, fontWeight: 750, color: "#001B48", marginBottom: 3 },
  docSub: { fontSize: 12.5, color: "#64748b", marginBottom: 5, lineHeight: 1.3 },
  docHint: { fontSize: 11.5, fontWeight: 600 },
};

/* ══════════════════════════════════════════════════════════════════
   Field wrapper
══════════════════════════════════════════════════════════════════ */
function Field({ label, children }) {
  return (
    <div style={S.field}>
      <div style={S.label}>{label}</div>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Global CSS
══════════════════════════════════════════════════════════════════ */
const css = `
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html, body, #root { height: 100%; width: 100%; }
  body {
    overflow-x: hidden;
    font-family: 'Segoe UI', ui-sans-serif, system-ui, -apple-system, sans-serif;
    background: #f4f6f9;
    color: #0f172a;
  }
  button, input, select { font-family: inherit; }
  input:focus, select:focus {
    outline: none;
    border-color: #f59e0b !important;
    box-shadow: 0 0 0 3px rgba(245,158,11,0.15);
  }
  select { appearance: auto; }
  .grid2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  @media (max-width: 640px) {
    .grid2 { grid-template-columns: 1fr !important; }
  }
  .avatarCircle:hover .avatarOverlay { opacity: 1 !important; }
`;

/* ══════════════════════════════════════════════════════════════════
   Styles
══════════════════════════════════════════════════════════════════ */
const S = {
  page: { minHeight: "100vh", background: "#f4f6f9" },
  container: {
    width: "min(960px,100%)", margin: "0 auto",
    padding: "24px 16px 80px",
    display: "flex", flexDirection: "column", gap: 14,
  },
  alert: {
    padding: "12px 16px", borderRadius: 12, background: "#fff",
    border: "1px solid rgba(15,23,42,.10)",
    boxShadow: "0 2px 8px rgba(15,23,42,.05)",
    color: "#0f172a", fontSize: 14,
  },
  alertErr: { borderColor: "rgba(239,68,68,.3)", background: "rgba(239,68,68,.06)", color: "#991b1b" },
  alertOk: { borderColor: "rgba(34,197,94,.3)", background: "rgba(34,197,94,.06)", color: "#166534" },
  header: {
    background: "#fff", borderRadius: 16,
    border: "1px solid rgba(15,23,42,.09)",
    boxShadow: "0 2px 12px rgba(15,23,42,.06)",
    padding: "18px 20px",
  },
  headerLeft: { display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" },
  headerInfo: { flex: 1, minWidth: 0 },
  headerName: { fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 6 },
  pills: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 },
  pill: {
    padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: "rgba(34,211,238,.12)", border: "1px solid rgba(34,211,238,.3)", color: "#075985",
  },
  pill2: {
    padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: "rgba(245,158,11,.10)", border: "1px solid rgba(245,158,11,.25)", color: "#92400e",
  },
  headerSub: {
    fontSize: 12.5, color: "#475569",
    display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
  },
  dot: { opacity: 0.4 },
  section: {
    background: "#fff", borderRadius: 16,
    border: "1px solid rgba(15,23,42,.09)",
    boxShadow: "0 2px 12px rgba(15,23,42,.06)",
    padding: "18px 20px",
  },
  sectionTitle: {
    fontSize: 14, fontWeight: 800, color: "#92400e",
    marginBottom: 14, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
  },
  icon: { fontSize: 16 },
  readonlyBadge: {
    fontSize: 11, fontWeight: 700,
    padding: "3px 10px", borderRadius: 999,
    background: "rgba(239,68,68,.07)",
    border: "1px solid rgba(239,68,68,.20)",
    color: "#b91c1c", marginLeft: 4,
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 11.5, fontWeight: 700, color: "#64748b",
    textTransform: "uppercase", letterSpacing: "0.04em",
  },
  input: {
    height: 42, borderRadius: 10,
    border: "1px solid rgba(15,23,42,.13)",
    background: "#fafafa", color: "#0f172a",
    padding: "0 12px", fontSize: 13.5,
    transition: "border-color .15s, box-shadow .15s",
    width: "100%",
  },
  roBox: {
    height: 42, borderRadius: 10,
    border: "1px solid rgba(15,23,42,.08)",
    background: "#f1f5f9",
    display: "flex", alignItems: "center",
    padding: "0 12px", justifyContent: "space-between", gap: 6,
  },
  roValue: { fontSize: 13.5, color: "#475569", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  roLock: { fontSize: 14, flexShrink: 0, opacity: 0.55 },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" },
  btnReset: {
    padding: "10px 20px", borderRadius: 10,
    border: "1px solid rgba(15,23,42,.13)",
    background: "#f1f5f9", color: "#334155",
    fontWeight: 700, fontSize: 13.5, cursor: "pointer",
  },
  btnSave: {
    padding: "10px 28px", borderRadius: 10,
    border: "1px solid rgba(245,158,11,.3)",
    background: "rgba(245,158,11,.12)", color: "#92400e",
    fontWeight: 800, fontSize: 13.5, cursor: "pointer",
  },
};